import React, { Component } from "react";
import {
  Tag,
  Input,
  Space,
  message,
  Select,
  Switch,
  PageHeader,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import {
  HeaderItem,
  ButtonItem,
  ModalItem,
  TableItemDinamico,
} from "../../../Components/Items";
import TabbedDynamicTable from "../../../Components/TabbedDynamicTable";

const coloresEstado = {
  PENDIENTE: "blue",
  APROBADA: "green",
  CONSUMIDA: "green",
  RECHAZADA: "red",
  EXPIRADA: "orange",
  CANCELADA: "default",
};

// Pantalla del autorizador: solicitudes pendientes por resolver, historial,
// configuración del PIN personal y (master) configuración de autorizadores.
// Se actualiza en vivo con los eventos WS "autorizacion_refresh"/"autorizacion_update"
// reenviados por Panel.js.
class Autorizaciones extends Component {
  state = {
    loading: false,
    pendientes: [],
    historial: [],
    totalHistorial: 0,
    pageHistorial: 1,
    pageSizeHistorial: 50,
    estatus: { tienePin: false, esAutorizador: false, permisosQueAutoriza: [] },
    // Modal para resolver: {solicitud, decision}
    modalResolver: null,
    motivoRechazo: "",
    pinResolver: "",
    resolviendo: false,
    // Configuración de PIN
    pinNuevo: "",
    passwordActual: "",
    guardandoPin: false,
    // Configuración de autorizadores (solo master)
    configPermisos: [],
    configUsuarios: [],
    guardandoConfig: null, // permisoId que se está guardando
  };

  componentDidMount() {
    this.consultarEstatus();
    this.consultarPendientes();
    if (this.props.tipo === 0 || this.props.permisoConfigurarAutorizaciones) {
      this.consultarConfiguracion();
    }
    window.addEventListener("autorizacion_refresh", this.onRefreshWS);
    window.addEventListener("autorizacion_update", this.onRefreshWS);
  }

  componentWillUnmount() {
    window.removeEventListener("autorizacion_refresh", this.onRefreshWS);
    window.removeEventListener("autorizacion_update", this.onRefreshWS);
  }

  onRefreshWS = () => {
    this.consultarPendientes();
    this.consultarHistorial({
      page: this.state.pageHistorial,
      pageSize: this.state.pageSizeHistorial,
    });
  };

  consultarEstatus = async () => {
    try {
      const res = await axios.post(
        "/autorizaciones/estatusPin",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({
          estatus: {
            tienePin: res.data.tienePin,
            esAutorizador: res.data.esAutorizador,
            permisosQueAutoriza: res.data.permisosQueAutoriza || [],
          },
        });
      }
    } catch (error) {
      console.error("estatusPin", error);
    }
  };

  consultarPendientes = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "/autorizaciones/selectPendientes",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({ pendientes: res.data.solicitudes });
      }
    } catch (error) {
      console.error("selectPendientes", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  // Recibe el requestBody de TableItemDinamico: {page, pageSize, ...}
  consultarHistorial = async (params = {}) => {
    const page = params.page || 1;
    const pageSize = params.pageSize || this.state.pageSizeHistorial;
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "/autorizaciones/selectHistorial",
        { empresaId: this.props.empresaId, page, limite: pageSize },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({
          historial: res.data.solicitudes,
          totalHistorial: res.data.total,
          pageHistorial: page,
          pageSizeHistorial: pageSize,
        });
      }
    } catch (error) {
      console.error("selectHistorial", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  abrirResolver = (solicitud, decision) => {
    this.setState({
      modalResolver: { solicitud, decision },
      motivoRechazo: "",
      pinResolver: "",
    });
  };

  resolver = async () => {
    const { modalResolver, motivoRechazo, pinResolver } = this.state;
    if (!modalResolver) return;
    const { solicitud, decision } = modalResolver;

    if (decision === "APROBAR" && solicitud.requierePinRemoto && !/^\d{6}$/.test(pinResolver)) {
      return message.warning("Ingresa tu código de autorizador de 6 dígitos");
    }

    this.setState({ resolviendo: true });
    try {
      const res = await axios.post(
        "/autorizaciones/resolverSolicitud",
        {
          empresaId: this.props.empresaId,
          solicitudId: solicitud._id,
          decision,
          motivo: motivoRechazo || undefined,
          pin: pinResolver || undefined,
        },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        message.success(decision === "APROBAR" ? "Solicitud aprobada" : "Solicitud rechazada");
        this.setState({ modalResolver: null });
        this.onRefreshWS();
      }
    } catch (error) {
      message.error(error?.response?.data?.mensaje || "No se pudo resolver la solicitud");
    } finally {
      this.setState({ resolviendo: false });
    }
  };


  consultarConfiguracion = async () => {
    try {
      const res = await axios.post(
        "/autorizaciones/selectConfiguracion",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({
          configPermisos: res.data.permisos.map((p) => ({
            ...p,
            autorizadores: p.autorizadores.map((id) => id.toString()),
          })),
          configUsuarios: res.data.usuarios,
        });
      }
    } catch (error) {
      console.error("selectConfiguracion", error);
    }
  };

  actualizarConfigLocal = (permisoId, cambios) => {
    this.setState({
      configPermisos: this.state.configPermisos.map((p) =>
        p.permisoId === permisoId ? { ...p, ...cambios } : p
      ),
    });
  };

  guardarTodaConfiguracion = async () => {
    this.setState({ guardandoConfig: true });
    try {
      for (const permiso of this.state.configPermisos) {
        const res = await axios.post(
          "/autorizaciones/guardarConfiguracion",
          {
            empresaId: this.props.empresaId,
            permisoId: permiso.permisoId,
            autorizadores: permiso.autorizadores,
            requierePinRemoto: permiso.requierePinRemoto,
          },
          { headers: this.props.headersToken }
        );
        if (res.data.success !== 2) {
          throw new Error(`No se pudo guardar "${permiso.etiqueta}"`);
        }
      }
      message.success("Configuración de autorizadores guardada");
    } catch (error) {
      message.error(
        error?.response?.data?.mensaje || error.message || "No se pudo guardar la configuración"
      );
    } finally {
      this.setState({ guardandoConfig: null });
    }
  };

  guardarPin = async () => {
    const { pinNuevo, passwordActual } = this.state;
    if (!/^\d{6}$/.test(pinNuevo)) {
      return message.warning("El código debe ser de 6 dígitos");
    }
    if (!passwordActual) {
      return message.warning("Ingresa tu contraseña para confirmar");
    }
    this.setState({ guardandoPin: true });
    try {
      const res = await axios.post(
        "/autorizaciones/configurarPin",
        { empresaId: this.props.empresaId, pin: pinNuevo, passwordActual },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        message.success("Código de autorizador guardado");
        this.setState({ pinNuevo: "", passwordActual: "" });
        this.consultarEstatus();
      }
    } catch (error) {
      message.error(error?.response?.data?.mensaje || "No se pudo guardar el código");
    } finally {
      this.setState({ guardandoPin: false });
    }
  };

  renderDetalle = (record) => {
    const contexto = record.contexto || {};
    return (
      <div style={{ fontSize: 12 }}>
        {contexto.descripcion && <div>{contexto.descripcion}</div>}
        {contexto.accesoLibre ? (
          <div><Tag color="orange">Acceso libre</Tag></div>
        ) : (
          (contexto.valorActual !== undefined && contexto.valorActual !== null) && (
            <div>
              Valor actual: <b>{String(contexto.valorActual)}</b>
              {contexto.valorSolicitado !== undefined && contexto.valorSolicitado !== null && (
                <span> → Solicitado: <b>{contexto.valorSolicitadoTexto || String(contexto.valorSolicitado)}</b></span>
              )}
            </div>
          )
        )}
        {contexto.folio && <div>{contexto.modulo} {contexto.folio}</div>}
        {typeof contexto.monto === "number" && (
          <div>Monto: <b>${contexto.monto.toLocaleString("en-US", { minimumFractionDigits: 2 })}</b></div>
        )}
        {contexto.comentario && <div>Comentario: {contexto.comentario}</div>}
      </div>
    );
  };


  renderTabPendientes() {
    const columnas = [
      {
        title: "Fecha",
        dataIndex: "createdAt",
        key: "createdAt",
        width: "12%",
        render: (v) => moment(v).format("DD/MM/YYYY HH:mm"),
      },
      { title: "Solicitante", dataIndex: "solicitanteNombre", key: "solicitanteNombre", width: "16%" },
      { title: "Operación", dataIndex: "etiqueta", key: "etiqueta", width: "18%" },
      { title: "Detalle", key: "detalle", render: (_, r) => this.renderDetalle(r) },
      {
        title: "Expira",
        dataIndex: "fechaExpiracion",
        key: "fechaExpiracion",
        width: "10%",
        render: (v) => moment(v).fromNow(),
      },
      {
        title: "Acciones",
        key: "acciones",
        width: "18%",
        render: (_, record) => (
          <Space>
            <ButtonItem
              verde
              size="small"
              icon={<CheckOutlined />}
              onClick={() => this.abrirResolver(record, "APROBAR")}
            >
              Aprobar
            </ButtonItem>
            <ButtonItem
              rojo
              size="small"
              icon={<CloseOutlined />}
              onClick={() => this.abrirResolver(record, "RECHAZAR")}
            >
              Rechazar
            </ButtonItem>
          </Space>
        ),
      },
    ];

    return (
      <TableItemDinamico
        columns={columnas}
        dataSource={this.state.pendientes}
        totalRecords={this.state.pendientes.length}
        loading={this.state.loading}
        onFetchData={() => this.consultarPendientes()}
        locale={{ emptyText: "No hay solicitudes pendientes" }}
      />
    );
  }

  renderTabHistorial() {
    const columnas = [
      {
        title: "Fecha",
        dataIndex: "createdAt",
        key: "createdAt",
        width: "12%",
        render: (v) => moment(v).format("DD/MM/YYYY HH:mm"),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        width: "10%",
        render: (v) => <Tag color={coloresEstado[v] || "default"}>{v}</Tag>,
      },
      {
        title: "Modo",
        dataIndex: "modo",
        key: "modo",
        width: "8%",
        render: (v) => (v === "PIN" ? "Código" : "Remota"),
      },
      { title: "Operación", dataIndex: "etiqueta", key: "etiqueta", width: "16%" },
      { title: "Solicitante", dataIndex: "solicitanteNombre", key: "solicitanteNombre", width: "14%" },
      { title: "Autorizador", dataIndex: "autorizadorNombre", key: "autorizadorNombre", width: "14%" },
      { title: "Detalle", key: "detalle", render: (_, r) => this.renderDetalle(r) },
    ];

    return (
      <TableItemDinamico
        columns={columnas}
        dataSource={this.state.historial}
        totalRecords={this.state.totalHistorial}
        loading={this.state.loading}
        defaultPageSize={50}
        onFetchData={(params) => this.consultarHistorial(params)}
        locale={{ emptyText: "Sin historial todavía" }}
      />
    );
  }

  renderTabPin() {
    const { estatus } = this.state;
    return (
      <div style={{ maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <HeaderItem>Código de autorizador</HeaderItem>
          {estatus.tienePin
            ? <Tag color="green">Configurado</Tag>
            : <Tag color="orange">Sin configurar</Tag>}
        </div>
        <p style={{ fontSize: 12, color: "#888" }}>
          Tu código personal de 6 dígitos te identifica al autorizar operaciones
          (presencial o al confirmar aprobaciones remotas). No lo compartas.
        </p>
        {estatus.permisosQueAutoriza.length > 0 && (
          <p style={{ fontSize: 12 }}>
            Puedes autorizar:{" "}
            {estatus.permisosQueAutoriza.map((p) => (
              <Tag key={p.permisoId}>{p.etiqueta}</Tag>
            ))}
          </p>
        )}
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input.Password
            placeholder={estatus.tienePin ? "Nuevo código (6 dígitos)" : "Código (6 dígitos)"}
            value={this.state.pinNuevo}
            maxLength={6}
            onChange={(e) =>
              this.setState({ pinNuevo: e.target.value.replace(/\D/g, "").slice(0, 6) })
            }
          />
          <Input.Password
            placeholder="Tu contraseña (para confirmar)"
            value={this.state.passwordActual}
            onChange={(e) => this.setState({ passwordActual: e.target.value })}
          />
          <ButtonItem
            verde
            block
            loading={this.state.guardandoPin}
            onClick={this.guardarPin}
            icon={<SaveOutlined />}
          >
            {estatus.tienePin ? "Cambiar código" : "Guardar código"}
          </ButtonItem>
        </Space>
      </div>
    );
  }

  renderTabConfiguracion() {
    const columnas = [
      { title: "Operación", dataIndex: "etiqueta", key: "etiqueta", width: "22%" },
      {
        title: "Autorizadores",
        key: "autorizadores",
        render: (_, permiso) => (
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Selecciona usuarios autorizadores"
            value={permiso.autorizadores}
            onChange={(valores) =>
              this.actualizarConfigLocal(permiso.permisoId, { autorizadores: valores })
            }
            optionFilterProp="children"
          >
            {this.state.configUsuarios.map((u) => (
              <Select.Option
                key={u._id}
                value={u._id.toString()}
                disabled={!u.tienePin}
              >
                {`${u.nombre} ${u.apellidoPaterno || ""}`}
                {u.tienePin ? " 🔑" : " (sin código)"}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: "Remota requiere PIN",
        key: "requierePinRemoto",
        width: "15%",
        align: "center",
        render: (_, permiso) => (
          <Switch
            checked={permiso.requierePinRemoto}
            onChange={(checked) =>
              this.actualizarConfigLocal(permiso.permisoId, { requierePinRemoto: checked })
            }
          />
        ),
      },
    ];

    return (
      <>
        <p style={{ fontSize: 12, color: "#888" }}>
          Define qué usuarios pueden autorizar cada operación y si la aprobación
          remota exige confirmar con el código (PIN). La autorización presencial
          por código siempre requiere PIN. Solo puedes seleccionar usuarios que
          ya configuraron su código de autorizador (🔑).
        </p>
        <TableItemDinamico
          columns={columnas}
          dataSource={this.state.configPermisos}
          totalRecords={this.state.configPermisos.length}
          rowKey={(r) => r.permisoId}
          onFetchData={() => this.consultarConfiguracion()}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <ButtonItem
            verde
            icon={<SaveOutlined />}
            loading={!!this.state.guardandoConfig}
            onClick={this.guardarTodaConfiguracion}
          >
            Guardar todos los cambios
          </ButtonItem>
        </div>
      </>
    );
  }

  render() {
    const { modalResolver, resolviendo, pendientes } = this.state;

    const puedeVerHistorial = this.props.tipo === 0 || this.props.permisoVerHistorialAutorizaciones;
    const puedeConfigurar = this.props.tipo === 0 || this.props.permisoConfigurarAutorizaciones;

    const tabs = [
      {
        key: "pendientes",
        label: `Pendientes (${pendientes.length})`,
        customContent: this.renderTabPendientes(),
      },
      puedeVerHistorial && {
        key: "historial",
        label: "Historial",
        customContent: this.renderTabHistorial(),
      },
      {
        key: "pin",
        label: "Mi código",
        customContent: this.renderTabPin(),
      },
      puedeConfigurar && {
        key: "configuracion",
        label: "Configuración",
        customContent: this.renderTabConfiguracion(),
      },
    ].filter(Boolean);

    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 12,
          padding: 20,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        }}
      >
        <PageHeader
          title="Autorizaciones"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <ButtonItem
              blanco
              size="small"
              icon={<ReloadOutlined />}
              onClick={this.onRefreshWS}
              lessTime
            >
              Actualizar
            </ButtonItem>
          }
        />
        <TabbedDynamicTable
          tabs={tabs}
          defaultActiveKey="pendientes"
        />

        <ModalItem
          title={modalResolver?.decision === "APROBAR" ? "Aprobar solicitud" : "Rechazar solicitud"}
          visible={!!modalResolver}
          onCancel={() => this.setState({ modalResolver: null })}
          onOk={this.resolver}
          confirmLoading={resolviendo}
          okText={modalResolver?.decision === "APROBAR" ? "Aprobar" : "Rechazar"}
          footer
          destroyOnClose
          width={520}
          spinning={false}
          paddingTop
        >
          {modalResolver && (
            <>
              <p>
                <b>{modalResolver.solicitud.solicitanteNombre}</b> solicita:{" "}
                <b>{modalResolver.solicitud.etiqueta}</b>
              </p>
              {this.renderDetalle(modalResolver.solicitud)}
              {modalResolver.decision === "RECHAZAR" && (
                <Input.TextArea
                  placeholder="Motivo del rechazo (opcional)"
                  value={this.state.motivoRechazo}
                  onChange={(e) => this.setState({ motivoRechazo: e.target.value })}
                  rows={2}
                  style={{ marginTop: 12 }}
                />
              )}
              {modalResolver.decision === "APROBAR" && modalResolver.solicitud.requierePinRemoto && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ marginBottom: 4 }}>
                    Esta operación requiere confirmar con tu código de autorizador:
                  </p>
                  <Input.Password
                    placeholder="••••••"
                    maxLength={6}
                    value={this.state.pinResolver}
                    onChange={(e) =>
                      this.setState({ pinResolver: e.target.value.replace(/\D/g, "").slice(0, 6) })
                    }
                    style={{ width: 160, letterSpacing: 6 }}
                  />
                </div>
              )}
            </>
          )}
        </ModalItem>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  tipo: state.user.tipo,
  permisoVerHistorialAutorizaciones:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) =>
        valueTodos.permisoId.toString() === "6a4e7b26c1cbb316e2e6fa0d"
    )?.activado,
  permisoConfigurarAutorizaciones:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) =>
        valueTodos.permisoId.toString() === "6a4e7b32c1cbb316e2e6fa16"
    )?.activado,
});

export default connect(mapStateToProps, {})(Autorizaciones);
